<template>
  <div class="borrowerAttributeAddress">

    <BorrowerAttributeConstructor
      :questionText="questionText"
      :description="description"
      :descriptionTooltip="descriptionTooltip"
      :tooltipText="tooltipText"
      :errorText="errorText"
      :helpText="helpText"
      :disclaimer="disclaimer">
      <template v-slot:input>
        <div class="d-grid ba-address-container">
          <div class="row">
            <div class="col">
              <div class="form-floating">
                <!--  Using type="search" instead of type="text" on address input field disables address input autofill while still allowing google places autocomplete. autocomplete="nope" does not work in this case since google places autocomplete overrides and sets autocomplete="off" - autocomplete="off" does not work for chrome browser. -->
                <input
                  v-model="autocompleteText"
                  :class="{'is-invalid': _get(v$, 'address.street.value.$error') || (_get(v$, 'autocompleteText.$dirty') && _get(v$, 'autocompleteText.$invalid'))}"
                  :id="'ba' + address.street.attributeId"
                  :name="'ba' + address.street.attributeId"
                  :data-cy="address.street.alias"
                  type="search"
                  ref="autocomplete"
                  class="form-control"
                  :placeholder="mappedAddressLabels.street"
                  @focus="onFocus()"
                  :disabled="disabled"
                  @blur="onBlur(v$.address.street.value)"
                  @change="onAutoComplete(); onChange(v$.autocompleteText, $event, 'street')"/>
                <label class="type-400" :for="'ba-' + address.street.attributeId">
                  <span>{{ mappedAddressLabels.street }}</span>
                </label>
                <span class="invalid-feedback" v-if="mappedErrorMessages.street">
                  <i class="fa-light fa-circle-exclamation"></i> {{ mappedErrorMessages.street }}
                </span>
                <span class="invalid-feedback" v-if="mappedErrorMessages.pobox">
                  <i class="fa-light fa-circle-exclamation"></i> {{ mappedErrorMessages.pobox }}
                </span>
              </div>
            </div>
          </div>
          <div class="row">
            <div class="col">
              <div class="form-floating">
                <input
                  ref="city"
                  v-model="address.city.value"
                  :id="'ba' + address.city.attributeId"
                  :name="'ba' + address.city.attributeId"
                  :data-cy="address.city.alias"
                  :class="{'is-invalid': _get(v$, 'address.city.value.$error') || (_get(v$, 'address.city.value.$dirty') && _get(v$, 'address.city.value.$invalid'))}"
                  type="text"
                  class="form-control"
                  :placeholder="mappedAddressLabels.city"
                  :disabled="disabled"
                  @change="onChange(v$.address.city.value, $event, 'city')"
                  @blur="onBlur(v$.address.city.value)"/>
                <label class="type-400" :for="'ba-' + address.city.attributeId">
                  <span>{{ mappedAddressLabels.city }}</span>
                </label>
                <span class="invalid-feedback" v-if="mappedErrorMessages.city">
                  <i class="fa-light fa-circle-exclamation"></i> {{ mappedErrorMessages.city }}
                </span>
              </div>
            </div>
          </div>
          <div class="row marketplace-state-zip">
            <div class="col-12 col-sm-6">
              <div class="form-floating">
                <select class="form-select mb-5 mb-sm-0"
                        v-model="address.stateId.value"
                        :id="'ba' + address.stateId.attributeId"
                        :name="'ba' + address.stateId.attributeId"
                        :data-cy="address.stateId.alias"
                        :disabled="disabled"
                        :class="{
                          'is-invalid': _get(v$, 'address.stateId.value.$dirty') && _get(v$, 'address.stateId.value.$invalid'),
                          'no-value-select': !address.stateId.value
                        }"
                        @blur="onBlur(v$.address.stateId.value)"
                        @change="onChange(v$.address.stateId.value, $event, 'stateId')"
                        @click="setShowLabel(true)"
                >
                  <option :value="null" disabled selected>{{ mappedAddressLabels.state }}</option>
                  <option
                    v-for="(option, i) in options"
                    :value="option.value"
                    :key="i">
                    {{ option.label }}
                  </option>
                </select>
                <label class="type-400" v-if="showLabel || address.stateId.value" :for="'ba-' + address.stateId.attributeId">
                  <span>{{ mappedAddressLabels.state }}</span>
                </label>
                <span class="invalid-feedback" v-if="mappedErrorMessages.state">
                  <i class="fa-light fa-circle-exclamation"></i> {{ mappedErrorMessages.state }}
                </span>
              </div>
            </div>

            <div class="col-12 col-sm-6">
              <div class="form-floating">
                <input
                  v-model="address.zipId.value"
                  :id="'ba' + address.zipId.attributeId"
                  :name="'ba' + address.zipId.attributeId"
                  :data-cy="address.zipId.alias"
                  :class="{'is-invalid': _get(v$, 'address.zipId.value.$dirty') && _get(v$, 'address.zipId.value.$invalid')}"
                  v-maska data-maska="#####"
                  masked=true
                  type="text"
						      inputmode="numeric"
						      pattern="[0-9]*"
                  class="form-control form-zip required"
                  :placeholder="mappedAddressLabels.zip"
                  :disabled="disabled"
                  @blur="onBlur(v$.address.zipId.value)"
                  @change="onChange(v$.address.zipId.value, $event, 'zipId')">
                <label class="type-400" :for="'ba-' + address.zipId.attributeId">
                  <span>{{ mappedAddressLabels.zip }}</span>
                </label>
                <div class="invalid-feedback" v-if="mappedErrorMessages.zip">
                  <i class="fa-light fa-circle-exclamation"></i> {{ mappedErrorMessages.zip }}
                </div>
              </div>
            </div>
          </div>
        </div>
      </template>
    </BorrowerAttributeConstructor>
  </div>
</template>

<script>
import {lsUtils} from '~/libs/ls-utils-service.js'
import LoadScript, {GOOGLE_MAPS} from '../../libs/script-loader'

import { useVuelidate } from '@vuelidate/core'
import { required, minLength, numeric, helpers } from '@vuelidate/validators'
const { withAsync, withMessage } = helpers

import get from 'lodash/get'
import { zipValidator } from '~/libs/validators/zipValidator'
import BaseBorrowerAttributeMixin from './BaseBorrowerAttributeMixin'

let loggedUnmappedKeys = {}

const getAddressKey = function (alias) {
  switch (alias) {
    case 'owner_street_address':
    case 'borrower.street':
      return 'street'
    case 'borrowerMailingStreet':
      return 'street'
    case 'owner_city':
    case 'borrower.city':
      return 'city'
    case 'borrowerMailingCity':
      return 'city'
    case 'owner_state':
    case 'borrower.stateId':
      return 'stateId'
    case 'borrowerMailingState':
      return 'stateId'
    case 'owner_zip':
    case 'borrower.zipId':
      return 'zipId'
    case 'borrowerMailingZip':
      return 'zipId'
    // Unimplemented/extra fields in address component
    case 'ownerCounty':
    case 'ownerCountry':
    case 'owner_street_address_':
    case 'borrower.street2':
    case 'borrower_street_address_':
    case 'owner_street_address_three':
    case 'owner_zip_extra':
    case 'uk_owner_postcode':
      return
    default:
      loggedUnmappedKeys[alias] = loggedUnmappedKeys[alias] || (console.error(new Error('No address key found for: '+ alias)), true)
  }
}


export default {
  emits: ['invalidity','inputChange','placechanged','answer','change','focus'],
  setup: () => ({ v$: useVuelidate() }),
  data () {
    return {
      options: lsUtils.getUsStateOptions(),
      autocomplete: null,
      autocompleteText: '',
      showLabel: null,
      address: this.formatAddress(),
      value: '',
    }
  },
  created () {
    this.$watch('invalid', (invalid) => {
      this.$emit('invalidity', invalid)
    }, {immediate: true})

    this.autocompleteText = this.address && this.address.street.value || ''
  },
  computed: {
    errorText() {
      return ''
    },
    invalid() {
      return this.v$.$invalid
    },
    mappedAddressLabels() {
      if (this.question.alias === 'businessAddress') {
        return {
          street: 'Business Street Address',
          city: 'Business City',
          state: 'Business State',
          zip: 'Business Zip Code',
        }
      } else if (this.question.alias === 'ownerAddress') {
        return {
          street: "Owner's Street Address",
          city: "Owner's City",
          state: "Owner's State",
          zip: "Owner's Zip Code",
        }
      } else if (this.question.alias === 'mailingAddress') {
        return {
          street: "Mailing Street Address",
          city: "Mailing City",
          state: "Mailing State",
          zip: "Mailing Zip Code",
        }
      } else {
        return {
          street: 'Street Address',
          city: 'City',
          state: 'State',
          zip: 'Zip Code',
        }
      }
    },
    mappedErrorMessages() {
      const messages = {}
      if(!get(this.v$, 'address')){
        return messages
      }
      if (get(this.v$, 'address.street.value.$error') || get(this.v$, 'autocompleteText.$error')) {
        messages.street = get(this.v$, 'address.street.value.$errors[0].$message')
      }
      if (get(this.v$, 'address.city.value.$error')) {
        messages.city = get(this.v$, 'address.city.value.$errors[0].$message')
      }
      if (get(this.v$, 'address.stateId.value.$error')) {
        messages.state = get(this.v$, 'address.stateId.value.$errors[0].$message')
      }
      if (!get(this.v$, 'address.zipId.value.minLength') || get(this.v$, 'address.zipId.$error')) {
        messages.zip = get(this.v$, 'address.zipId.value.$errors[0].$message')
      }
      return messages
    }
  },
  mixins: [BaseBorrowerAttributeMixin],
  validations () {
    return {
      autocompleteText: {
        required,
        minLength: minLength(3)
      },
      address: {
        street: {
          value: {
            required: withMessage('Enter a street address', required),
            minLength: minLength(3),
          },
        },
        city: {
          value: {
            required: withMessage('Enter a city', required),
            minLength: minLength(3)
          }
        },
        stateId: {
          value: {
            required: withMessage('Select a state', required),
            minLength: minLength(2)
          }
        },
        zipId: {
          value: {
            required: withMessage('Enter a zip code', required),
            minLength: withMessage('Zip codes must be 5 digits', minLength(5)),
            zipExists: withAsync(
              withMessage('Enter a valid zip code', zipValidator(this.$axios))
            )
          }
        }
      },
      $validationGroups: {
        fullAddress: ['autocompleteText', 'address.city.value', 'address.stateId.value', 'address.zipId.value']
      }
    }
  },
  watch: {
    autocompleteText: function (oldVal, newVal) {
      this.address.street.value = oldVal
      this.$emit('inputChange', { newVal, oldVal })
    },
    'question.components': function (components, oldComponents) {
      const reduceQuestions = (carry, c) => (carry[c.alias] = c.value, carry)
      const oldMapping = oldComponents.reduce(reduceQuestions, {}),
            newMapping = components.reduce(reduceQuestions, {})
      components.forEach(c => {
        let alias = c.alias, val = newMapping[alias] || null, key = getAddressKey(alias)
        if (!key || oldMapping[alias] === val || val === this.address[key].value) {
          return
        }
        if (key === 'street') {
          this.autocompleteText = val
        }
        this.address[key].value = val
      })
    },
  },
  async mounted () {
    try {
      await LoadScript(GOOGLE_MAPS)
      this.autocomplete = new window.google.maps.places.Autocomplete(this.$refs.autocomplete, { types: ['address'] })

    } catch (err) {
      log.warning("Failed to wire up Google places autocomplete in BorrowerAttribute", { err })
      return
    }

    this.autocomplete.addListener('place_changed', this.placeChanged)
  },
  methods: {
    _get() {
      return get(...arguments)
    },
    formatAddress() {
      return this.question.components.reduce((address, c) => {
        address[getAddressKey(c.alias)] = {
          alias: c.alias,
          value: c.value || null,
          id: c.id,
          attributeId: c.attributeId || c.id,
        }
        return address
      }, {})
    },
    placeChanged () {
      let place = this.autocomplete.getPlace()

      if (!place || !place.address_components) return

      let addressComponents = {
        street_number: 'short_name',
        route: 'long_name',
        locality: 'long_name',
        administrative_area_level_1: 'short_name',
        postal_code: 'short_name'
      }

      let returnData = {}

      if (place.address_components !== 'undefined') {
        for (let i = 0; i < place.address_components.length; i++) {
          let addressType = place.address_components[i].types[0]
          if (addressComponents[addressType]) {
            returnData[addressType] = place.address_components[i][addressComponents[addressType]]
          }
        }
      }
      this.$emit('placechanged', returnData, place, this.id)

      if (returnData.street_number && returnData.route) {
        this.autocompleteText = `${returnData.street_number} ${returnData.route}`
      } else if (!returnData.street_number && returnData.route) {
        this.autocompleteText = returnData.route
      }

      this.address.street.value = this.autocompleteText
      this.address.city.value = returnData.locality
      this.address.zipId.value = returnData.postal_code
      if (place.formatted_address.includes('Puerto Rico')) {
        returnData.administrative_area_level_1 = 'PR'
      }
      this.address.stateId.value = returnData.administrative_area_level_1

      Object.keys(this.address).forEach(key => {
        let question = {
          alias: this.address[key].alias,
          value: this.address[key].value || null
        }
        if (question.value && question.alias) {
          this.$emit('answer', { value: question, isInvalid: false })
        } else {
          this.$emit('answer', { value: question, isInvalid: true })
        }
      })
    },
    onAutoComplete () {
      this.$emit('change', this.autocompleteText)
    },
    onFocus () {
      this.$emit('focus')
    },
    focus () {
      this.$refs.autocomplete(focus)
    },
    onChange (v$, e, model) {
      v$.$touch()
      let question = {
        alias: this.address[model].alias,
        value: this.address[model].value || null
      }

      if (!this.address.city.value || !this.address.stateId.value || !this.address.street.value || !this.address.zipId.value || this.address.city.value === "" || this.address.stateId.value === "" || this.address.street.value === "" || this.address.zipId.value === "" ) {
        this.$emit('invalidity', true)
      } else {
        this.$emit('invalidity', v$.$invalid)
      }
      this.$emit('answer', { value: question, isInvalid: v$.$invalid })
    },
    onBlur (v$) {
      !this.address.stateId.value && this.setShowLabel(false)
      v$.$touch()
    },
    setShowLabel (payload) {
      this.showLabel = payload
    }
  },
  props: {
    disabled: Boolean,
  }
}
</script>

<style lang="scss" scoped>

.ba-address-container {
  > *:not(:last-child) {
    margin-bottom: 24px;
  }
}

</style>
